import { useState } from 'react';
import { useTranslation } from 'react-i18next';


import {
  Button,
  ButtonsWrapper,
  DeleteModal,
  ICONS,
  PLATFORM_THEME_COLORS,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import { PERMISSION_ACTIONS, PLATFORM_PERMISSIONS } from '@thinknet-gmbh/platform-common';


import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetHealthInsurance } from 'src/hooks/health-insurance/get-health-insurance.hook';
import { useDeleteHealthInsuranceById } from 'src/hooks/health-insurance/delete-health-insurance-by-id.hook';

import PermissionCheck from 'src/components/permission-check';
import { DrawerComponent } from 'src/components/custom-drawer/';
import { HealthInsuranceTable } from './components/health-insurance-table';


import {
  DEFAULT_HEALTH_INSURANCE,
  HEALTH_INSURANCE_MODALS,
} from './health-insurance.constants';

import { HealthInsuranceWrapper } from './health-insurance.styles';
import ChangeHealthInsurance from './components/change-health-insurance/change-health-insurance.component';

const HealthInsurance = () => {
  const { t } = useTranslation('settings');
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [healthInsurance, setHealthInsurance] = useState(
    DEFAULT_HEALTH_INSURANCE
  );

  const currentCompanyId = useGetCurrentCompanyId();
  const { collection, paginationConfig, onTableChange, loading } = useGetHealthInsurance(
    currentCompanyId
  );

  const handleCreateHealthInsurance = () => {
    setActiveModal(HEALTH_INSURANCE_MODALS.create);
  };

  const closeModal = () => {
    setActiveModal(null);
    setHealthInsurance(DEFAULT_HEALTH_INSURANCE);
  };

  const deleteHealthInsuranceById = useDeleteHealthInsuranceById({
    onSuccess: closeModal,
  });

  const deleteEmployeeQuestionnaireSetting = () => {
    deleteHealthInsuranceById({
      variables: { id: healthInsurance?._id },
    });
    setHealthInsurance(DEFAULT_HEALTH_INSURANCE);
    closeModal();
  };

  const onSubmitDelete = () => {
    deleteEmployeeQuestionnaireSetting();
  };

  const isCreate = activeModal === HEALTH_INSURANCE_MODALS.create;
  const isEdit = activeModal === HEALTH_INSURANCE_MODALS.edit;

  return (
    <HealthInsuranceWrapper>
      <DrawerComponent
        title={t(`healthInsurance.labels.${isCreate ? 'addNewHealthInsurance' : 'editHealthInsurance'}`)}
        subtitle={t(
          `healthInsurance.labels.${isCreate ? 'addNewHealthInsuranceSubtitle' : 'editHealthInsuranceSubtitle'}`
        )}
        onClose={closeModal}
        visible={isEdit || isCreate}
        width={388}
        destroyOnClose
      >
      <ChangeHealthInsurance onClose={closeModal} id={healthInsurance._id}/>
      </DrawerComponent>
      <DeleteModal
        translate={t}
        isOpened={activeModal === HEALTH_INSURANCE_MODALS.delete}
        title={t('healthInsurance.deleteModal.title')}
        subTitle={t('healthInsurance.deleteModal.subtitle', {
          healthInsuranceName: healthInsurance.name,
        })}
        onClose={closeModal}
        onSubmit={onSubmitDelete}
      />
      <div>
        <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
          {t('healthInsurance.labels.healthInsurance')}
        </Typography>
        <PermissionCheck
          permission={PLATFORM_PERMISSIONS.companySettings}
          action={PERMISSION_ACTIONS.create}
        >
          <ButtonsWrapper>
            <Button
              type="primary"
              ghost
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={handleCreateHealthInsurance}
            >
              {t('healthInsurance.buttons.addHealthInsurance')}
            </Button>
          </ButtonsWrapper>
        </PermissionCheck>
      </div>
      <HealthInsuranceTable
        dataSource={collection}
        paginationConfig={paginationConfig}
        onTableChange={onTableChange}
        handleModals={(modal, record) => {
          setActiveModal(modal);
          setHealthInsurance(record);
        }}
        loading={loading}
      />
    </HealthInsuranceWrapper>
  );
};

export default HealthInsurance;
